import { forwardRef, useMemo } from "react";
import { tv } from "tailwind-variants";

import { useControlledState } from "../../hooks/useControlledState";
import { cn } from "../../utils/cn";

import type { ComponentPropsWithoutRef } from "react";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

const colorSwatchPickerVariants = tv({
  slots: {
    base: "flex flex-wrap gap-2",
    swatch: "size-6 cursor-pointer rounded",
  },
  variants: {
    isSelected: {
      true: {
        swatch:
          "border-box outline-solid border-2 border-solid border-white outline outline-gray-900",
      },
    },
  },
});

export interface ColorSwatchPickerProps
  extends ComponentPropsWithoutRef<"div">,
    Omit<VariantProps<typeof colorSwatchPickerVariants>, "isSelected"> {
  /** Classes to apply to the slots */
  classNames?: SlotsToClasses<keyof ReturnType<typeof colorSwatchPickerVariants>>;
  /** Colors to display in the picker, in hex format */
  colorOptions: string[];
  selectedColor?: string;
  defaultColor?: string;
  onSelectColor?: (color: string) => void;
}

/**
 * A basic color picker that allows users to select a color from a set of predefined options.
 */
export const ColorSwatchPicker = forwardRef<HTMLDivElement, ColorSwatchPickerProps>(
  (
    {
      className,
      classNames,
      colorOptions,
      selectedColor: selectedColorProp,
      defaultColor,
      onSelectColor,
      ...props
    },
    ref
  ) => {
    const [selectedColor, setSelectedColor] = useControlledState(
      selectedColorProp,
      defaultColor ?? "",
      onSelectColor
    );

    const slots = useMemo(() => colorSwatchPickerVariants(), []);

    return (
      <div
        ref={ref}
        className={slots.base({ className: cn(classNames?.base, className) })}
        {...props}
      >
        {colorOptions.map(color => (
          <ColorSwatch
            key={color}
            className={slots.swatch({
              isSelected: color === selectedColor,
              className: classNames?.swatch,
            })}
            color={color}
            onClick={() => setSelectedColor(color)}
          />
        ))}
      </div>
    );
  }
);
ColorSwatchPicker.displayName = "ColorSwatchPicker";

interface ColorSwatchProps {
  color: string;
  className: string;
  onClick: () => void;
}

const ColorSwatch = forwardRef<HTMLDivElement, ColorSwatchProps>(
  ({ className, color, onClick }, ref) => {
    return (
      <div ref={ref} className={className} style={{ backgroundColor: color }} onClick={onClick} />
    );
  }
);
ColorSwatch.displayName = "ColorSwatch";
